import { useEffect } from "react"
import type { SetURLSearchParams } from "react-router"

/**
 * `source=nav` marks a redirect from another detail view (vs. a normal list
 * click). Seeds the search field with the item's name so it shows up in the
 * list, then strips the param so subsequent interactions behave normally.
 *
 * `seedSearchValue` is the already-resolved string (or `undefined` while the
 * detail fetch is pending/absent) rather than a callback, so this effect's
 * deps stay plain, stable values — a caller-supplied derive function would
 * be a fresh reference every render and defeat the dependency check.
 */
export function useNavSeedSearch({
  isNavRedirect,
  selectedId,
  detailLoaded,
  detailErrored,
  seedSearchValue,
  setSearch,
  setSearchParams,
}: {
  isNavRedirect: boolean
  selectedId: string | null
  detailLoaded: boolean
  detailErrored: boolean
  seedSearchValue: string | undefined
  setSearch: (value: string) => void
  setSearchParams: SetURLSearchParams
}) {
  useEffect(() => {
    if (!isNavRedirect) return
    if (selectedId && !detailLoaded && !detailErrored) return
    if (seedSearchValue !== undefined) setSearch(seedSearchValue)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("source")
        return next
      },
      { replace: true },
    )
  }, [
    isNavRedirect,
    selectedId,
    detailLoaded,
    detailErrored,
    seedSearchValue,
    setSearch,
    setSearchParams,
  ])
}
